import * as React from 'react';
import { z } from 'zod';
import { Alert } from './Alert';
import { Button } from './Button';
import { Card, CardBody, CardHeader } from './Card';
import { Form, FormField, applyServerErrors, useZodForm } from './Form';
import { Input } from './Input';
import { Radio, RadioGroup } from './RadioGroup';
import { Loading } from './Spinner';

export interface RoadSyncLocationOption {
  id: string;
  name: string;
  /**
   * Whether the location can take Relay's itemised invoices. False means it
   * can't (or RoadSync could not say), so it is shown but cannot be picked.
   * Null or missing means it was not checked, and it can be picked.
   */
  canInvoice?: boolean | null;
}

/** What the backend says when a location can't take Relay's invoices. */
export const ROADSYNC_LOCATION_CANT_INVOICE =
  "This RoadSync location can't take invoices from Relay yet. Ask RoadSync support to turn on multi-line items for it, or pick another location.";

/** A location can be picked unless RoadSync said it can't take invoices. */
export function isRoadSyncLocationSelectable(
  location: RoadSyncLocationOption
): boolean {
  return location.canInvoice !== false;
}

/** The id to start on: the first location that can be picked, or ''. */
export function firstSelectableRoadSyncLocation(
  locations: RoadSyncLocationOption[]
): string {
  return locations.find(isRoadSyncLocationSelectable)?.id ?? '';
}

export interface RoadSyncLocationRadiosProps {
  locations: RoadSyncLocationOption[];
  value: string;
  onValueChange: (id: string) => void;
  disabled?: boolean;
}

/**
 * One radio per RoadSync location. A location that can't take invoices is
 * shown locked, with the reason under its name, so the owner sees why it
 * is not on offer.
 */
export function RoadSyncLocationRadios({
  locations,
  value,
  onValueChange,
  disabled = false,
}: RoadSyncLocationRadiosProps) {
  return (
    <RadioGroup value={value} onValueChange={onValueChange} disabled={disabled}>
      {locations.map((loc) => {
        const locked = !isRoadSyncLocationSelectable(loc);
        return (
          <Radio
            key={loc.id}
            value={loc.id}
            label={loc.name}
            disabled={locked}
            description={locked ? "Can't take invoices yet" : undefined}
          />
        );
      })}
    </RadioGroup>
  );
}

/** What a submit resolves to when the account has more than one location. */
export interface RoadSyncConnectResult {
  needsLocationSelection?: boolean;
  locations?: RoadSyncLocationOption[];
}

export interface RoadSyncConnectFormProps {
  /** Resolves once connected; resolve a result instead to ask for a location. */
  onSubmit: (apiKey: string) => Promise<RoadSyncConnectResult | void>;
  onSelectLocation?: (locationId: string) => Promise<void>;
  /** The screen is busy on the form's behalf: the button spins and locks. */
  isLoading?: boolean;
  /** An error from the screen, shown above the form. */
  error?: string | null;
}

/**
 * Connecting RoadSync: an API key, then — when the account has several — a
 * choice of location, with a spinner between the two while the key is
 * checked.
 *
 *   <RoadSyncConnectForm onSubmit={connect} onSelectLocation={pickLocation}
 *     isLoading={connecting} error={error} />
 *
 * The form validates its own fields and reports its own failures; `error`
 * is for what the screen learns after the promise rejects.
 */
const apiKeySchema = z.object({
  apiKey: z.string().trim().min(1, 'API Key is required'),
});

const locationSchema = z.object({
  locationId: z.string().min(1, 'Select a location to continue'),
});

export function RoadSyncConnectForm({
  onSubmit,
  onSelectLocation,
  isLoading = false,
  error = null,
}: RoadSyncConnectFormProps) {
  const [locations, setLocations] = React.useState<RoadSyncLocationOption[]>(
    []
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const keyForm = useZodForm(apiKeySchema, { defaultValues: { apiKey: '' } });
  const locationForm = useZodForm(locationSchema, {
    defaultValues: { locationId: '' },
  });

  const busy = isSubmitting || isLoading;

  const handleSubmit = async ({ apiKey }: { apiKey: string }) => {
    setIsSubmitting(true);
    try {
      const result = await onSubmit(apiKey);
      if (result?.needsLocationSelection && result.locations?.length) {
        setLocations(result.locations);
        locationForm.setValue(
          'locationId',
          firstSelectableRoadSyncLocation(result.locations)
        );
        setIsSubmitting(false);
      }
    } catch (e) {
      applyServerErrors(keyForm, e, 'Connection failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleSelectLocation = async ({
    locationId,
  }: {
    locationId: string;
  }) => {
    setIsSubmitting(true);
    try {
      await onSelectLocation?.(locationId);
    } catch (e) {
      applyServerErrors(
        locationForm,
        e,
        'Could not save that location. Please try again.'
      );
      setIsSubmitting(false);
    }
  };

  if (isSubmitting && locations.length === 0) {
    return (
      <Loading
        size="xl"
        tone="primary"
        pad="sm"
        title="Connecting to RoadSync"
        description="Please wait while we establish the connection"
      />
    );
  }

  /* What the screen learned after its own promise rejected, which is not
     about any one field. The form's own failures sit on the form. */
  const screenError = error ? <Alert tone="destructive">{error}</Alert> : null;

  if (locations.length > 0) {
    const anySelectable = locations.some(isRoadSyncLocationSelectable);
    return (
      <Card className="w-full">
        <CardHeader
          title="Choose a location"
          description="Payments will be created at this RoadSync location."
        />
        <CardBody>
          <Form form={locationForm} gap={4} onSubmit={handleSelectLocation}>
            {screenError}

            {!anySelectable && (
              <Alert tone="warning">{ROADSYNC_LOCATION_CANT_INVOICE}</Alert>
            )}

            <FormField form={locationForm} name="locationId">
              {(field) => (
                <RoadSyncLocationRadios
                  locations={locations}
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={busy}
                />
              )}
            </FormField>

            {anySelectable && (
              <Button fullWidth type="submit" disabled={busy} loading={busy}>
                {busy ? 'Connecting...' : 'Continue'}
              </Button>
            )}
          </Form>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader
        title="RoadSync Connection"
        description="Enter your RoadSync credentials to establish a connection"
      />
      <CardBody>
        <Form form={keyForm} gap={4} onSubmit={handleSubmit}>
          {screenError}

          <FormField
            form={keyForm}
            name="apiKey"
            label="API Key"
            description={
              <>Found in your RoadSync dashboard under Settings &rarr; API</>
            }
          >
            {(field) => (
              <Input
                type="password"
                placeholder="Enter your RoadSync API Key"
                {...field}
                disabled={busy}
              />
            )}
          </FormField>

          <Button fullWidth type="submit" disabled={busy} loading={busy}>
            {busy ? 'Connecting...' : 'Connect'}
          </Button>
        </Form>
      </CardBody>
    </Card>
  );
}
